<template>
  <div class='metrics-page'>
    <header class='metrics-header'>
      <div class='metrics-heading'>
        <button class='back-link' @click="$router.push({ name: 'ManageMvps' })">
          <b-icon icon='chevron-left' /> Voltar
        </button>
        <div class='title-row'>
          <h1 class='metrics-title'>{{ mvp ? mvp.title : 'Métricas do MVP' }}</h1>
          <b-badge v-if='mvp' :variant='mvp.active ? "success" : "secondary"' class='status-badge'>
            {{ mvp.active ? 'Ativo' : 'Inativo' }}
          </b-badge>
        </div>
      </div>
      <button class='btn-export' :disabled='loading || exporting || !mvp' @click='exportXlsx'>
        <b-spinner v-if='exporting' small />
        <b-icon v-else icon='download' />
        Exportar planilha
      </button>
    </header>

    <section class='filter-bar'>
      <div class='presets'>
        <button
          v-for='days in presets'
          :key='days'
          class='preset'
          :class='{ active: isPresetActive(days) }'
          @click='setPreset(days)'
        >
          {{ days }} dias
        </button>
      </div>
      <div class='filter-divider' />
      <div class='date-range'>
        <label class='date-field'>
          De
          <input v-model='startDate' type='date' :max='endDate'>
        </label>
        <b-icon icon='arrow-right' class='range-arrow' />
        <label class='date-field'>
          Até
          <input v-model='endDate' type='date' :min='startDate'>
        </label>
      </div>
      <span class='range-summary'>{{ rangeSummary }}</span>
      <div class='filter-divider' />
      <label class='search-field'>
        <b-icon icon='search' />
        <input v-model='search' placeholder='Pesquisar por nome ou e-mail' @keyup.enter='applyFilters'>
      </label>
      <div class='flex-spacer' />
      <button class='btn-ghost' :disabled='isDefaultFilters || loading || !mvp' @click='clearFilters'>
        <b-icon icon='x-circle' /> Limpar filtros
      </button>
      <button class='btn-ghost' :disabled='loading || !mvp' @click='loadAll'>
        <b-icon icon='arrow-clockwise' /> Atualizar
      </button>
      <button class='btn-apply' :disabled='!filtersChanged || loading || !mvp' @click='applyFilters'>Aplicar</button>
    </section>

    <p v-if='notFound' class='empty-state'>MVP não encontrado.</p>

    <template v-else>
      <div v-if='loadingSummary' class='stats-row'>
        <div class='stats-grid'>
          <div v-for='n in 4' :key='n' class='stat-card'>
            <b-skeleton width='60%' />
            <b-skeleton height='40px' width='40%' />
          </div>
        </div>
        <div class='feedback-card'>
          <b-skeleton width='35%' />
          <b-skeleton height='40px' width='30%' />
          <b-skeleton height='8px' />
        </div>
      </div>

      <div v-else-if='metrics' class='stats-row'>
        <div class='stats-grid'>
          <div v-for='tile in tiles' :key='tile.label' class='stat-card'>
            <span class='stat-label'>{{ tile.label }}</span>
            <span class='stat-value' :class='{ "stat-value--text": tile.text }'>{{ tile.value }}</span>
          </div>
        </div>

        <div class='feedback-card'>
          <div class='feedback-head'>
            <span class='stat-label'>Ajudaria?</span>
            <span class='muted'>Cliques no aviso: <strong>{{ metrics.summary.bannerClicks }}</strong></span>
          </div>
          <div class='feedback-value'>
            <template v-if='metrics.summary.positivePct !== null'>
              <span class='stat-value'>{{ metrics.summary.positivePct }}<span class='stat-unit'>%</span></span>
              <span class='muted'>positivo</span>
            </template>
            <span v-else class='muted'>Nenhuma resposta até agora</span>
          </div>
          <div class='feedback-progress'>
            <div class='feedback-bar'>
              <div class='feedback-bar-fill' :style='{ width: `${metrics.summary.positivePct || 0}%` }' />
            </div>
            <div class='feedback-legend'>
              <span><i class='dot dot--green' />Sim · {{ metrics.summary.likes }}</span>
              <span><i class='dot dot--gray' />Não · {{ metrics.summary.dislikes }}</span>
            </div>
          </div>
        </div>
      </div>

      <section class='panel'>
        <div class='panel-head'>
          <h2 class='panel-title'>Acessos por dia</h2>
          <div class='chart-legend'>
            <span><i class='dot dot--sapphire' />Acessos</span>
            <span><i class='dot dot--teal' />Usuários únicos</span>
          </div>
        </div>
        <div class='panel-body'>
          <b-skeleton-img v-if='loadingSummary' no-aspect height='200px' />
          <mvp-daily-chart v-else-if='metrics && metrics.perDay.length' :data='metrics.perDay' :height='200' />
          <p v-else class='table-empty'>{{ searchActive ? noSearchResults : 'Nenhum acesso no período' }}</p>
        </div>
      </section>

      <section class='panel'>
        <div class='panel-head'>
          <h2 class='panel-title'>Quem usou</h2>
        </div>
        <div class='table-scroll'>
          <table class='data-table'>
            <thead>
              <tr>
                <th>E-mail</th>
                <th>Telefone</th>
                <th>Plano</th>
                <th>1º acesso</th>
                <th>Último acesso</th>
                <th class='text-right'>Acessos</th>
                <th class='text-right'>Tempo total</th>
              </tr>
            </thead>
            <tbody v-if='loadingUsers'>
              <tr v-for='n in skeletonRows' :key='n'>
                <td v-for='c in 7' :key='c'><b-skeleton /></td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for='user in users' :key='user.userId'>
                <td class='strong'>{{ user.email || '-' }}</td>
                <td class='muted'>{{ user.phone || '-' }}</td>
                <td><span v-if='user.planCode' class='pill'>{{ formatPlan(user) }}</span><span v-else>-</span></td>
                <td class='muted'>{{ formatDate(user.firstAccess) }}</td>
                <td class='muted'>{{ formatDate(user.lastAccess) }}</td>
                <td class='text-right'>{{ user.views }}</td>
                <td class='text-right'>{{ formatDuration(user.totalSeconds) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if='!loadingUsers && !users.length' class='table-empty'>
            {{ searchActive ? noSearchResults : 'Nenhum usuário no período' }}
          </p>
        </div>
        <div v-if='usersMeta.total > pageSize' class='panel-foot'>
          <b-pagination
            :value='usersPage'
            :total-rows='usersMeta.total'
            :per-page='pageSize'
            size='sm'
            class='mb-0'
            @change='onUsersPage'
          />
        </div>
      </section>

      <section class='panel'>
        <div class='panel-head panel-head--bordered'>
          <h2 class='panel-title'>Cliques dentro do MVP</h2>
          <span class='count-pill'>{{ eventsMeta.total || 0 }}</span>
        </div>
        <div class='table-scroll'>
          <table class='data-table'>
            <thead>
              <tr>
                <th>Elemento clicado</th>
                <th class='col-clicks'>Cliques</th>
                <th class='text-right col-users'>Usuários únicos</th>
              </tr>
            </thead>
            <tbody v-if='loadingEvents'>
              <tr v-for='n in skeletonRows' :key='n'>
                <td v-for='c in 3' :key='c'><b-skeleton /></td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for='event in events' :key='event.name'>
                <td class='wrap'>{{ event.name }}</td>
                <td>
                  <div class='click-bar'>
                    <span class='click-count'>{{ event.count }}</span>
                    <div class='click-track'>
                      <div class='click-fill' :style='{ width: `${(event.count / clicksMax) * 100}%` }' />
                    </div>
                  </div>
                </td>
                <td class='text-right muted'>{{ event.uniqueUsers }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if='!loadingEvents && !events.length' class='table-empty'>
            {{ searchActive ? noSearchResults : 'Nenhum clique no período' }}
          </p>
        </div>
        <div v-if='eventsMeta.total > pageSize' class='panel-foot'>
          <b-pagination
            :value='eventsPage'
            :total-rows='eventsMeta.total'
            :per-page='pageSize'
            size='sm'
            class='mb-0'
            @change='onEventsPage'
          />
        </div>
      </section>

      <section class='panel'>
        <div class='panel-head panel-head--bordered'>
          <h2 class='panel-title'>Comentários</h2>
          <span class='count-pill'>{{ feedbacksMeta.total || 0 }}</span>
        </div>
        <div class='table-scroll'>
          <table class='data-table'>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>E-mail</th>
                <th>Ajudaria?</th>
                <th class='col-comment'>Comentário</th>
                <th class='text-right'>Data</th>
              </tr>
            </thead>
            <tbody v-if='loadingFeedbacks'>
              <tr v-for='n in skeletonRows' :key='n'>
                <td v-for='c in 5' :key='c'><b-skeleton /></td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for='feedback in feedbacks' :key='feedback._id' class='align-top'>
                <td class='strong'>{{ feedback.name || '-' }}</td>
                <td class='muted'>{{ feedback.email || '-' }}</td>
                <td>
                  <span class='verdict' :class='feedback.liked ? "verdict--yes" : "verdict--no"'>
                    <i class='dot' />{{ formatLiked(feedback.liked) }}
                  </span>
                </td>
                <td class='wrap'>{{ feedback.comment || '-' }}</td>
                <td class='text-right muted'>{{ formatDate(feedback.updatedAt) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if='!loadingFeedbacks && !feedbacks.length' class='table-empty'>
            {{ searchActive ? noSearchResults : 'Nenhum retorno até agora' }}
          </p>
        </div>
        <div v-if='feedbacksMeta.total > pageSize' class='panel-foot'>
          <b-pagination
            :value='feedbacksPage'
            :total-rows='feedbacksMeta.total'
            :per-page='pageSize'
            size='sm'
            class='mb-0'
            @change='onFeedbacksPage'
          />
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import XLSX from 'xlsx'
import MvpDailyChart from '@/components/Mvp/MvpDailyChart.vue'

const DATE_FORMAT = 'YYYY-MM-DD'
const MAX_RANGE_DAYS = 366
const DEFAULT_PRESET_DAYS = 30

export default {
  name: 'MvpMetrics',
  components: { MvpDailyChart },
  data() {
    const today = moment().format(DATE_FORMAT)
    const start = moment().subtract(29, 'days').format(DATE_FORMAT)
    return {
      mvp: null,
      notFound: false,
      presets: [7, 30, 90],
      startDate: start,
      endDate: today,
      search: '',
      applied: { startDate: start, endDate: today, filter: '' },
      noSearchResults: 'Nenhum resultado para essa busca.',
      loadingMvp: true,
      loadingSummary: true,
      loadingUsers: true,
      loadingEvents: true,
      loadingFeedbacks: true,
      exporting: false,
      pageSize: 10,
      skeletonRows: 5,
      metrics: null,
      users: [],
      usersMeta: {},
      events: [],
      eventsMeta: {},
      feedbacks: [],
      feedbacksMeta: {}
    }
  },
  computed: {
    mvpId() {
      return this.$route.params.id
    },
    loading() {
      return this.loadingMvp
        || this.loadingSummary
        || this.loadingUsers
        || this.loadingEvents
        || this.loadingFeedbacks
    },
    rangeDays() {
      const start = moment(this.startDate, DATE_FORMAT, true)
      const end = moment(this.endDate, DATE_FORMAT, true)
      if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
        return 0
      }
      return end.diff(start, 'days') + 1
    },
    rangeSummary() {
      if (!this.rangeDays) {
        return 'Período inválido'
      }
      return this.rangeDays === 1 ? '1 dia' : `${this.rangeDays} dias`
    },
    filtersChanged() {
      return this.startDate !== this.applied.startDate
        || this.endDate !== this.applied.endDate
        || this.search.trim() !== this.applied.filter
    },
    searchActive() {
      return !!this.applied.filter
    },
    isDefaultFilters() {
      const range = this.presetRange(DEFAULT_PRESET_DAYS)
      return !this.search.trim()
        && this.startDate === range.startDate
        && this.endDate === range.endDate
        && !this.filtersChanged
    },
    tiles() {
      const summary = this.metrics.summary
      return [
        { label: 'Acessos', value: summary.views },
        { label: 'Usuários únicos', value: summary.uniqueUsers },
        { label: 'Tempo médio / acesso', value: this.formatDuration(summary.avgSecondsPerVisit), text: true },
        { label: 'Cliques no MVP', value: summary.totalClicks }
      ]
    },
    clicksMax() {
      return Math.max(1, this.eventsMeta.maxCount || 0)
    },
    usersPage() {
      return this.pageOf(this.usersMeta)
    },
    eventsPage() {
      return this.pageOf(this.eventsMeta)
    },
    feedbacksPage() {
      return this.pageOf(this.feedbacksMeta)
    }
  },
  methods: {
    ...mapActions('mvp', {
      handleGetMvp: 'handleGetMvp',
      handleGetMvpMetrics: 'handleGetMvpMetrics',
      handleGetMvpMetricsUsers: 'handleGetMvpMetricsUsers',
      handleGetMvpMetricsEvents: 'handleGetMvpMetricsEvents',
      handleGetMvpMetricsFeedbacks: 'handleGetMvpMetricsFeedbacks',
      handleExportMvpMetrics: 'handleExportMvpMetrics'
    }),
    ...mapActions('breadcrumb', {
      setBreadcrumb: 'setBreadcrumb'
    }),
    notify(message, variant) {
      this.$bvToast.toast(message, {
        title: 'Atenção',
        variant,
        toaster: 'b-toaster-top-center',
        autoHideDelay: 5000
      })
    },
    errorMessage(error, fallback) {
      const body = error && error.response && error.response.data
      return (body && body.errors) || fallback
    },
    pageOf(meta) {
      const limit = meta.limit || this.pageSize
      return Math.floor((meta.skip || 0) / limit) + 1
    },
    presetRange(days) {
      return {
        startDate: moment().subtract(days - 1, 'days').format(DATE_FORMAT),
        endDate: moment().format(DATE_FORMAT)
      }
    },
    isPresetActive(days) {
      const range = this.presetRange(days)
      return this.startDate === range.startDate && this.endDate === range.endDate
    },
    setPreset(days) {
      const range = this.presetRange(days)
      this.startDate = range.startDate
      this.endDate = range.endDate
    },
    rangeIsValid() {
      if (!this.rangeDays) {
        this.notify('Informe um período válido.', 'warning')
        return false
      }
      if (this.rangeDays - 1 > MAX_RANGE_DAYS) {
        this.notify('O período pode ter no máximo 1 ano.', 'warning')
        return false
      }
      return true
    },
    applyFilters() {
      if (!this.filtersChanged || this.loading || !this.mvp) {
        return
      }
      if (this.rangeIsValid()) {
        this.applied = { startDate: this.startDate, endDate: this.endDate, filter: this.search.trim() }
        this.loadAll()
      }
    },
    clearFilters() {
      const range = this.presetRange(DEFAULT_PRESET_DAYS)
      this.startDate = range.startDate
      this.endDate = range.endDate
      this.search = ''
      this.applied = { ...range, filter: '' }
      this.loadAll()
    },
    async loadMvp() {
      this.loadingMvp = true
      try {
        this.mvp = await this.handleGetMvp(this.mvpId)
        this.notFound = !this.mvp
      } catch (error) {
        this.notFound = true
      } finally {
        this.loadingMvp = false
      }
    },
    async loadAll() {
      try {
        await Promise.all([
          this.loadSummary(),
          this.loadUsers(0),
          this.loadEvents(0),
          this.loadFeedbacks(0)
        ])
      } catch (error) {
        this.notify(this.errorMessage(error, 'Não foi possível carregar as métricas do MVP.'), 'danger')
      }
    },
    async loadSummary() {
      this.loadingSummary = true
      try {
        this.metrics = await this.handleGetMvpMetrics({ id: this.mvpId, ...this.applied })
      } finally {
        this.loadingSummary = false
      }
    },
    async loadUsers(skip) {
      this.loadingUsers = true
      try {
        const response = await this.handleGetMvpMetricsUsers({
          id: this.mvpId,
          skip,
          limit: this.pageSize,
          ...this.applied
        })
        this.users = response.data.users
        this.usersMeta = response.meta
      } finally {
        this.loadingUsers = false
      }
    },
    async loadEvents(skip) {
      this.loadingEvents = true
      try {
        const response = await this.handleGetMvpMetricsEvents({
          id: this.mvpId,
          skip,
          limit: this.pageSize,
          ...this.applied
        })
        this.events = response.data.events
        this.eventsMeta = response.meta
      } finally {
        this.loadingEvents = false
      }
    },
    async loadFeedbacks(skip) {
      this.loadingFeedbacks = true
      try {
        const response = await this.handleGetMvpMetricsFeedbacks({
          id: this.mvpId,
          skip,
          limit: this.pageSize,
          ...this.applied
        })
        this.feedbacks = response.data.feedbacks
        this.feedbacksMeta = response.meta
      } finally {
        this.loadingFeedbacks = false
      }
    },
    async onUsersPage(page) {
      try {
        await this.loadUsers((Math.max(page, 1) - 1) * this.pageSize)
      } catch (error) {
        this.notify(this.errorMessage(error, 'Não foi possível carregar os usuários.'), 'danger')
      }
    },
    async onEventsPage(page) {
      try {
        await this.loadEvents((Math.max(page, 1) - 1) * this.pageSize)
      } catch (error) {
        this.notify(this.errorMessage(error, 'Não foi possível carregar os cliques.'), 'danger')
      }
    },
    async onFeedbacksPage(page) {
      try {
        await this.loadFeedbacks((Math.max(page, 1) - 1) * this.pageSize)
      } catch (error) {
        this.notify(this.errorMessage(error, 'Não foi possível carregar os comentários.'), 'danger')
      }
    },
    async exportXlsx() {
      this.exporting = true
      try {
        const data = await this.handleExportMvpMetrics({ id: this.mvpId, ...this.applied })

        const users = data.users.map((user) => ({
          Nome: user.name || '',
          'E-mail': user.email || '',
          Telefone: user.phone || '',
          Empresa: user.company || '',
          Perfil: user.job || '',
          Plano: user.planCode || '',
          'Status do plano': user.planStatus || '',
          '1º acesso': this.formatDate(user.firstAccess),
          'Último acesso': this.formatDate(user.lastAccess),
          Acessos: user.views,
          'Tempo total (s)': user.totalSeconds,
          'Tempo total': this.formatDuration(user.totalSeconds),
          'Ajudaria?': this.formatLiked(user.liked),
          'Comentário': user.comment || ''
        }))
        const events = data.events.map((event) => ({
          'Elemento clicado': event.name,
          Cliques: event.count,
          'Usuários únicos': event.uniqueUsers
        }))
        const feedbacks = data.feedbacks.map((feedback) => ({
          Nome: feedback.name || '',
          'E-mail': feedback.email || '',
          'Ajudaria?': this.formatLiked(feedback.liked),
          'Comentário': feedback.comment || '',
          Data: this.formatDate(feedback.updatedAt)
        }))

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(users), 'Usuarios')
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(events), 'Cliques')
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(feedbacks), 'Comentarios')
        XLSX.writeFile(wb, `mvp-${data.mvp.slug}-metricas-${data.meta.startDate}_${data.meta.endDate}.xlsx`)
      } catch (error) {
        this.notify(this.errorMessage(error, 'Não foi possível exportar as métricas.'), 'danger')
      } finally {
        this.exporting = false
      }
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : '-'
    },
    formatDuration(seconds) {
      const total = Math.round(Number(seconds) || 0)
      if (!total) {
        return '-'
      }
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const rest = total % 60
      if (hours) {
        return `${hours}h ${String(minutes).padStart(2, '0')}min`
      }
      return minutes ? `${minutes}min ${String(rest).padStart(2, '0')}s` : `${rest}s`
    },
    formatLiked(liked) {
      if (liked === true) {
        return 'Sim'
      }
      return liked === false ? 'Não' : '-'
    },
    formatPlan(user) {
      return user.planStatus ? `${user.planCode} (${user.planStatus})` : user.planCode
    }
  },
  created() {
    this.setBreadcrumb({
      tab: '',
      page: 'Gestão de novos MVPs',
      subPage: {
        name: 'Gestão de novos MVPs',
        active: false
      },
      tabs: [
        {
          name: 'Métricas',
          active: true
        }
      ]
    })
  },
  async mounted() {
    await this.loadMvp()
    if (this.mvp) {
      await this.loadAll()
    }
  }
}
</script>

<style lang='scss' scoped>
$navy: #022245;
$sapphire: #00537b;
$blue: #028ded;
$green: #55bb00;
$teal: #04a7f3;
$surface: #ffffff;
$border: #e2e6ee;
$border-strong: #cfd6e0;
$divider: #eef1f6;
$gray-100: #f3f5f8;
$gray-200: #e9edf2;
$gray-300: #d5dbe3;
$fg1: #1f2d3d;
$fg2: #5b6b7f;
$fg3: #808080;
$success: #28a745;
$danger: #dc3545;

.metrics-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin: 8px 0 48px;
  color: $fg1;
}

.metrics-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.metrics-heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.back-link {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: $fg3;
  cursor: pointer;

  &:hover {
    color: $navy;
  }
}

.title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.status-badge {
  font-size: 13px;
  padding: 5px 10px;
}

.metrics-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: $teal;
}

.btn-export,
.btn-apply,
.btn-ghost,
.preset {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.btn-export {
  height: 40px;
  padding: 0 16px;
  background: $surface;
  border: 1px solid $sapphire;
  border-radius: 6px;
  color: $sapphire;

  &:hover:not(:disabled) {
    background: $sapphire;
    color: $surface;
  }
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: $surface;
  border: 1px solid $border;
  border-radius: 8px;
}

.presets {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: $gray-100;
  border-radius: 6px;
}

.preset {
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: $fg2;

  &.active {
    background: $sapphire;
    color: $surface;
    box-shadow: 0 1px 2px rgba(2, 34, 69, 0.12);
  }
}

.filter-divider {
  width: 1px;
  align-self: stretch;
  background: $divider;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-field {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $fg3;

  input {
    height: 36px;
    padding: 0 8px;
    border: 1px solid $border-strong;
    border-radius: 6px;
    font-size: 14px;
    letter-spacing: 0;
    text-transform: none;
    color: $fg1;
    background: $surface;
  }
}

.range-arrow {
  color: $gray-300;
}

.range-summary,
.muted {
  font-size: 14px;
  color: $fg3;
}

.flex-spacer {
  flex: 1;
}

.btn-apply {
  height: 36px;
  padding: 0 20px;
  border: 0;
  border-radius: 6px;
  background: $blue;
  color: $surface;

  &:hover:not(:disabled) {
    background: $sapphire;
  }
}

.btn-ghost {
  height: 36px;
  padding: 0 14px;
  border: 1px solid $border-strong;
  border-radius: 6px;
  background: $surface;
  color: $fg2;

  &:hover:not(:disabled) {
    border-color: $sapphire;
    color: $sapphire;
  }
}

.empty-state {
  margin: 48px 0;
  text-align: center;
  color: $fg3;
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.stats-grid {
  flex: 2 1 560px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.stat-card,
.feedback-card {
  background: $surface;
  border: 1px solid $border;
  border-top: 4px solid $sapphire;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.feedback-card {
  flex: 1 1 340px;
  justify-content: space-between;
  gap: 12px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: $sapphire;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-value {
  font-size: 40px;
  font-weight: 600;
  line-height: 1;
  color: $navy;
  font-variant-numeric: tabular-nums;

  &--text {
    font-size: 28px;
    line-height: 40px;
  }
}

.stat-unit {
  margin-left: 2px;
  font-size: 18px;
  font-weight: 400;
  color: $fg3;
}

.feedback-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;

  strong {
    color: $fg1;
    font-weight: 600;
  }
}

.feedback-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.feedback-progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.feedback-bar {
  display: flex;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: $gray-200;
}

.feedback-bar-fill {
  background: $green;
  transition: width 0.3s ease;
}

.feedback-legend,
.chart-legend {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: $fg2;
  font-variant-numeric: tabular-nums;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.feedback-legend {
  justify-content: space-between;
}

.chart-legend {
  font-size: 14px;
}

.dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;

  &--sapphire {
    background: $sapphire;
  }

  &--teal {
    background: $teal;
  }

  &--green {
    background: $green;
  }

  &--gray {
    background: $gray-300;
  }
}

.panel {
  background: $surface;
  border: 1px solid $border;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.panel-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;

  &--bordered {
    justify-content: flex-start;
    gap: 8px;
    border-bottom: 1px solid $divider;
  }
}

.panel-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: $navy;
}

.panel-body {
  padding: 0 20px 20px;
}

.panel-foot {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  border-top: 1px solid $divider;
}

.count-pill {
  padding: 0 8px;
  border-radius: 999px;
  background: $sapphire;
  font-size: 12px;
  font-weight: 600;
  color: $surface;
  line-height: 20px;
}

.search-field {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  margin: 0;
  padding: 0 12px;
  flex: 1 1 240px;
  max-width: 320px;
  border: 1px solid $border-strong;
  border-radius: 6px;
  color: $fg3;

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    font-size: 14px;
    color: $fg1;
    background: transparent;
  }
}

.table-scroll {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  thead tr {
    background: $sapphire;
    text-align: left;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $surface;
  }

  th {
    padding: 10px 12px;
    font-weight: 600;

    &:first-child {
      padding-left: 20px;
    }

    &:last-child {
      padding-right: 20px;
    }
  }

  tbody tr {
    border-top: 1px solid $divider;

    &:hover {
      background: $gray-100;
    }
  }

  td {
    padding: 12px;
    color: $fg1;

    &:first-child {
      padding-left: 20px;
    }

    &:last-child {
      padding-right: 20px;
    }
  }

  .strong {
    font-weight: 600;
  }

  .wrap {
    white-space: normal;
    min-width: 240px;
  }

  .align-top {
    vertical-align: top;
  }

  .col-clicks {
    width: 240px;
  }

  .col-users {
    width: 140px;
  }

  .col-comment {
    width: 45%;
  }
}

.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(2, 141, 237, 0.12);
  font-size: 12px;
  font-weight: 600;
  color: $sapphire;
}

.click-bar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.click-count {
  width: 24px;
  text-align: right;
  font-weight: 600;
}

.click-track {
  flex: 1;
  height: 6px;
  background: $gray-100;
  border-radius: 999px;
  overflow: hidden;
}

.click-fill {
  height: 100%;
  background: $blue;
  border-radius: 999px;
}

.verdict {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  white-space: nowrap;

  .dot {
    border-radius: 50%;
    background: currentColor;
  }

  &--yes {
    color: $success;
  }

  &--no {
    color: $danger;
  }
}

.table-empty {
  margin: 0;
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: $fg3;
  border-top: 1px solid $divider;
}
</style>
